import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {
  catchError,
  delay,
  filter,
  map,
  mergeMap,
  retryWhen,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { ACCESS_TOKEN, AuthenticationService } from './authentication.service';
import { isOwnApiUrl } from '../shared/api/api-origins';
import { SessionExpiredError } from './session-expired.error';
import { ToastrService } from 'ngx-toastr';
import { ModalService } from '../shared/services/modal.service';
import { signIn } from './map-redirect-modal/map-redirect-modal-text';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(null);
  private refreshTokenRetryCount = 0;
  private maxRefreshTokenRetries = 6;

  private isToastShown = false;

  constructor(
    private authService: AuthenticationService,
    private toast: ToastrService,
    private modalService: ModalService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    // Third-party hosts never see our access token. HttpClient is not used
    // only against our own backend - the sales-forecast trade-area call goes
    // to api.mapbox.com - and Mapbox authenticates by access_token query
    // param, so the header was pure leakage of a logged-in visitor's session.
    // Their 401 is skipped along with it: a rejected Mapbox token is not our
    // session expiring, and must not trigger a refresh or a sign-in modal.
    if (!isOwnApiUrl(req.url)) {
      return next.handle(req);
    }

    // Skip 401 handling for refresh token requests to avoid infinite loops
    const isRefreshTokenRequest = req.url.includes('/token/refresh');

    if (this.authService.hasAccessToken()) {
      const accessToken = this.authService.getTokens()[ACCESS_TOKEN];

      const authRequest = req.clone({
        headers: req.headers.set('Authorization', 'Bearer ' + accessToken),
      });

      return next.handle(authRequest).pipe(
        catchError((error: HttpErrorResponse) => {
          if (
            error.status === 401 &&
            this.authService.hasAccessToken() &&
            !isRefreshTokenRequest
          ) {
            return this.handle401Error(authRequest, next);
          } else if (error.status === 401 && isRefreshTokenRequest) {
            // Handle refresh token failure directly and increment counter
            this.refreshTokenRetryCount++;

            return throwError(() => error);
          } else if (error.status === 429 && !this.isToastShown) {
            this.toast
              .warning('Too many request, please try later')
              .onHidden.subscribe(() => (this.isToastShown = false));
            this.isToastShown = true;
          }

          return throwError(() => error);
        })
      );
    } else {
      return next.handle(req);
    }
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.refreshTokenInProgress) {
      this.refreshTokenInProgress = true;
      this.refreshTokenSubject.next(null);

      return this.refreshTokenWithLimit().pipe(
        switchMap((accessToken) => {
          this.refreshTokenInProgress = false;
          this.refreshTokenSubject.next(accessToken);
          return next.handle(this.addToken(req, accessToken)); // Retry the original request
        }),
        catchError((err) => {
          this.refreshTokenInProgress = false;
          // this.toast.info('Please log in again', 'Sign in')
          this.modalService.openModal(signIn);
          this.authService.setTokens('', ''); // Manually nullify tokens
          // this.authService.logout(); // Log out on refresh failure
          // The caller still fails, but as a session that ended rather than as
          // a 400 from an endpoint they never called: the sign-in modal above
          // is the outcome, and `SENTRY_OPTIONS.beforeSend` drops it.
          return throwError(() =>
            err instanceof SessionExpiredError
              ? err
              : new SessionExpiredError('the refresh was rejected', err)
          );
        })
      );
    } else {
      // Queue the request until the token refresh completes
      return this.refreshTokenSubject.pipe(
        filter((token): token is string => token !== null),
        take(1), // Only take the first valid token emitted
        switchMap((accessToken) => {
          // Retry the original request with the new token
          return next.handle(this.addToken(req, accessToken));
        })
      );
    }
  }

  /**
   * Whether asking again could plausibly get a different answer.
   *
   * A refresh that came back 400, 401 or 403 is a decision about the token we
   * sent, and it will be the same decision six times over: the retry only
   * delayed the sign-in modal by six seconds and multiplied one dead session
   * into a pile of identical reports. What the retry is actually for is the
   * request that never got an answer - status 0 for a dropped connection, or a
   * backend that was briefly unwell.
   */
  private isWorthRetrying(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) return false;
    return error.status === 0 || error.status >= 500;
  }

  private refreshTokenWithLimit(): Observable<string> {
    return this.authService.refreshToken().pipe(
      map((response) => response.object.tokens.access),
      retryWhen((errors) =>
        errors.pipe(
          mergeMap((err) => {
            if (!this.isWorthRetrying(err)) {
              return throwError(() => err);
            }

            this.refreshTokenRetryCount++;
            if (this.refreshTokenRetryCount >= this.maxRefreshTokenRetries) {
              return throwError(
                () => new Error('Max refresh attempts reached')
              );
            }
            // Continue retrying
            return of(err).pipe(delay(1000));
          })
        )
      ),
      tap(() => {
        this.refreshTokenRetryCount = 0; // Reset retry count on successful refresh
      })
    );
  }

  private addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    // Clone the request and add the new token
    return req.clone({
      headers: req.headers.set('Authorization', 'Bearer ' + token),
    });
  }
}
